/**
 * D3-based Sankey Diagram Component for Data Flow Visualization
 *
 * This component uses d3-sankey to render a proper Sankey diagram
 * showing data flow from enterprise systems to Neo4j schema.
 *
 * Layers:
 * - Layer 0: Enterprise Systems
 * - Layer 1: System Modules
 * - Layer 2: Source Tables
 * - Layer 3: (Reserved for columns - currently unused)
 * - Layer 4: Schema Nodes
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal, SankeyGraph, SankeyNode, SankeyLink } from 'd3-sankey';

// Types for our Sankey data
interface DataFlowNode {
  node_id: string;
  label: string;
  layer: number;
  node_type: 'system' | 'module' | 'table' | 'column' | 'schema_node' | 'schema_relationship';
  color: string;
  metadata?: Record<string, unknown>;
  questions_count: number;
}

interface DataFlowLink {
  source: string;
  target: string;
  value: number;
  color: string;
  questions_served: string[];
  confidence: 'high' | 'medium' | 'low';
}

interface D3SankeyDiagramProps {
  nodes: DataFlowNode[];
  links: DataFlowLink[];
  width?: number;
  height?: number;
  onNodeClick?: (node: DataFlowNode) => void;
  onLinkClick?: (link: DataFlowLink) => void;
}

// Node colors by type
const NODE_COLORS: Record<string, string> = {
  system: '#3b82f6',      // blue-500
  module: '#8b5cf6',      // purple-500
  table: '#06b6d4',       // cyan-500
  column: '#64748b',      // slate-500
  schema_node: '#10b981', // emerald-500
  schema_relationship: '#f43f5e', // rose-500
};

// Confidence colors for links
const CONFIDENCE_COLORS: Record<string, string> = {
  high: '#22c55e',   // green-500
  medium: '#f59e0b', // amber-500
  low: '#ef4444',    // red-500
};

// Layer names
const LAYER_NAMES = ['Systems', 'Modules', 'Tables', 'Columns', 'Schema'];

// D3 Sankey node type
type D3SankeyNode = SankeyNode<DataFlowNode, DataFlowLink>;
type D3SankeyLink = SankeyLink<DataFlowNode, DataFlowLink>;

export const D3SankeyDiagram: React.FC<D3SankeyDiagramProps> = ({
  nodes,
  links,
  width = 1200,
  height = 600,
  onNodeClick,
  onLinkClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width, height });
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    content: React.ReactNode;
  }>({ visible: false, x: 0, y: 0, content: null });

  // Handle container resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: containerWidth } = entry.contentRect;
        if (containerWidth > 0) {
          setDimensions({
            width: Math.max(containerWidth - 40, 800),
            height: Math.max(height, 500),
          });
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  // Render Sankey diagram
  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 40, right: 150, bottom: 40, left: 150 };
    const innerWidth = dimensions.width - margin.left - margin.right;
    const innerHeight = dimensions.height - margin.top - margin.bottom;

    // Create main group
    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Find nodes that have at least one link (source or target)
    const linkedNodeIds = new Set<string>();
    links.forEach((link) => {
      linkedNodeIds.add(link.source);
      linkedNodeIds.add(link.target);
    });

    // Separate connected nodes (for sankey layout) and unconnected schema nodes
    const connectedNodes = nodes.filter((node) => linkedNodeIds.has(node.node_id));
    const unconnectedSchemaNodes = nodes.filter(
      (node) => node.layer === 4 && !linkedNodeIds.has(node.node_id)
    );

    // Build node index map for link resolution (using connected nodes only)
    const nodeIndexMap = new Map<string, number>();
    connectedNodes.forEach((node, index) => {
      nodeIndexMap.set(node.node_id, index);
    });

    // Map layers to consecutive indices (handles gaps like 0,1,2,4 -> 0,1,2,3)
    const uniqueLayers = Array.from(new Set(connectedNodes.map(n => n.layer))).sort((a, b) => a - b);
    const layerToIndex = new Map<number, number>();
    uniqueLayers.forEach((layer, index) => {
      layerToIndex.set(layer, index);
    });
    const maxLayerIndex = uniqueLayers.length - 1;

    // Transform connected nodes for d3-sankey with mapped layer
    const sankeyNodes = connectedNodes.map((node, index) => ({
      ...node,
      index,
      depth: layerToIndex.get(node.layer) ?? 0,
      isConnected: true,
    }));

    // Transform links for d3-sankey (using numeric indices)
    const sankeyLinks: Array<{
      source: number;
      target: number;
      value: number;
      color: string;
      questions_served: string[];
      confidence: 'high' | 'medium' | 'low';
      originalSource: string;
      originalTarget: string;
    }> = [];

    links.forEach((link) => {
      const sourceIdx = nodeIndexMap.get(link.source);
      const targetIdx = nodeIndexMap.get(link.target);

      if (sourceIdx !== undefined && targetIdx !== undefined) {
        sankeyLinks.push({
          source: sourceIdx,
          target: targetIdx,
          value: Math.max(link.value, 0.1), // Ensure positive value
          color: link.color,
          questions_served: link.questions_served,
          confidence: link.confidence,
          originalSource: link.source,
          originalTarget: link.target,
        });
      }
    });

    // Skip if no valid links
    if (sankeyLinks.length === 0) {
      console.warn('No valid links found for Sankey diagram');
      return;
    }

    // Create sankey layout (using numeric indices, not nodeId)
    const sankeyLayout = sankey<typeof sankeyNodes[0], typeof sankeyLinks[0]>()
      .nodeWidth(20)
      .nodePadding(15)
      .nodeSort((a, b) => {
        // Sort by depth (mapped layer) first, then by label
        if (a.depth !== b.depth) return a.depth - b.depth;
        return a.label.localeCompare(b.label);
      })
      .nodeAlign((node) => node.depth) // Align by pre-computed depth
      .extent([[0, 0], [innerWidth, innerHeight]]);

    // Generate the sankey data for connected nodes
    const sankeyData = sankeyLayout({
      nodes: sankeyNodes.map(n => ({ ...n })), // Create copies to avoid mutation
      links: sankeyLinks.map(l => ({ ...l })), // Create copies to avoid mutation
    });

    // Calculate X position for schema column (rightmost layer)
    const schemaColumnX = innerWidth - 20; // nodeWidth = 20
    const nodeHeight = 18;
    const nodePadding = 8;

    // Add unconnected schema nodes manually positioned on the right
    const connectedSchemaNodesCount = sankeyData.nodes.filter(
      n => n.layer === 4
    ).length;

    // Sort unconnected schema nodes alphabetically
    unconnectedSchemaNodes.sort((a, b) => a.label.localeCompare(b.label));

    // Calculate starting Y position for unconnected nodes
    // Place them below the connected schema nodes
    let unconnectedStartY = 0;
    sankeyData.nodes.forEach(n => {
      if (n.layer === 4 && (n.y1 || 0) > unconnectedStartY) {
        unconnectedStartY = (n.y1 || 0) + nodePadding;
      }
    });
    if (unconnectedStartY === 0) {
      unconnectedStartY = 10; // Start from top if no connected schema nodes
    }

    // Create manually positioned nodes for unconnected schema
    const manualSchemaNodes = unconnectedSchemaNodes.map((node, idx) => ({
      ...node,
      x0: schemaColumnX,
      x1: schemaColumnX + 20,
      y0: unconnectedStartY + idx * (nodeHeight + nodePadding),
      y1: unconnectedStartY + idx * (nodeHeight + nodePadding) + nodeHeight,
      depth: maxLayerIndex,
      isConnected: false,
    }));

    // Combine all nodes for rendering
    const allRenderNodes = [...sankeyData.nodes, ...manualSchemaNodes];

    // Draw links
    const linkGroup = g.append('g').attr('class', 'links');

    linkGroup
      .selectAll('path')
      .data(sankeyData.links)
      .join('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('fill', 'none')
      .attr('stroke', (d) => {
        const confidence = (d as unknown as { confidence: string }).confidence;
        return CONFIDENCE_COLORS[confidence] || '#94a3b8';
      })
      .attr('stroke-opacity', 0.5)
      .attr('stroke-width', (d) => Math.max(1, d.width || 1))
      .style('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        d3.select(this).attr('stroke-opacity', 0.8);

        const sourceNode = d.source as D3SankeyNode;
        const targetNode = d.target as D3SankeyNode;
        const linkData = d as unknown as { confidence: string; value: number };

        setTooltip({
          visible: true,
          x: event.pageX,
          y: event.pageY,
          content: (
            <div className="text-sm">
              <p className="font-semibold">{sourceNode.label} → {targetNode.label}</p>
              <p className="text-neutral-500">Flow: {linkData.value} columns</p>
              <p className="capitalize">Confidence: {linkData.confidence}</p>
            </div>
          ),
        });
      })
      .on('mouseout', function () {
        d3.select(this).attr('stroke-opacity', 0.5);
        setTooltip((prev) => ({ ...prev, visible: false }));
      })
      .on('click', (event, d) => {
        if (onLinkClick) {
          const linkData = d as unknown as {
            originalSource: string;
            originalTarget: string;
            value: number;
            color: string;
            questions_served: string[];
            confidence: 'high' | 'medium' | 'low';
          };
          onLinkClick({
            source: linkData.originalSource,
            target: linkData.originalTarget,
            value: linkData.value,
            color: linkData.color,
            questions_served: linkData.questions_served,
            confidence: linkData.confidence,
          });
        }
      });

    // Draw nodes (including manually positioned unconnected schema nodes)
    const nodeGroup = g.append('g').attr('class', 'nodes');

    const nodeRects = nodeGroup
      .selectAll('g')
      .data(allRenderNodes)
      .join('g')
      .attr('transform', (d) => `translate(${d.x0},${d.y0})`)
      .style('cursor', 'pointer');

    // Node rectangles - unconnected nodes shown with reduced opacity
    nodeRects
      .append('rect')
      .attr('width', (d) => (d.x1 || 0) - (d.x0 || 0))
      .attr('height', (d) => (d.y1 || 0) - (d.y0 || 0))
      .attr('fill', (d) => NODE_COLORS[d.node_type] || '#64748b')
      .attr('fill-opacity', (d) => (d as { isConnected?: boolean }).isConnected === false ? 0.4 : 1)
      .attr('rx', 3)
      .attr('ry', 3)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1)
      .on('mouseover', function (event, d) {
        d3.select(this).attr('stroke-width', 2).attr('stroke', '#1e293b');

        setTooltip({
          visible: true,
          x: event.pageX,
          y: event.pageY,
          content: (
            <div className="text-sm">
              <p className="font-semibold">{d.label}</p>
              <p className="text-neutral-500 capitalize">{d.node_type.replace('_', ' ')}</p>
              <p>Layer: {LAYER_NAMES[d.layer]}</p>
              {d.questions_count > 0 && (
                <p className="text-green-600">
                  Serves {d.questions_count} question{d.questions_count !== 1 ? 's' : ''}
                </p>
              )}
              {d.metadata?.column_count !== undefined && (
                <p>Columns: {d.metadata.column_count as number}</p>
              )}
            </div>
          ),
        });
      })
      .on('mouseout', function () {
        d3.select(this).attr('stroke-width', 1).attr('stroke', '#ffffff');
        setTooltip((prev) => ({ ...prev, visible: false }));
      })
      .on('click', (event, d) => {
        if (onNodeClick) {
          onNodeClick(d);
        }
      });

    // Node labels
    //
    // Labels are drawn at each node's vertical centre. Thin nodes in the same
    // layer end up only a pixel or two apart, so their labels overlap and
    // become unreadable — worst in the schema column, where a dozen entities
    // share similar flow volumes. Nudge colliding labels apart per layer:
    // walk each layer top-to-bottom and push any label that would land within
    // LABEL_MIN_GAP of the previous one.
    const LABEL_MIN_GAP = 13; // ~font-size 11px + breathing room
    const labelOffsets = new Map<string, number>();
    const nodesByLayer = new Map<number, typeof allRenderNodes>();
    allRenderNodes.forEach((n) => {
      const list = nodesByLayer.get(n.layer) || [];
      list.push(n);
      nodesByLayer.set(n.layer, list);
    });
    nodesByLayer.forEach((layerNodes) => {
      const sorted = [...layerNodes].sort((a, b) => (a.y0 || 0) - (b.y0 || 0));
      let lastY = -Infinity;
      sorted.forEach((n) => {
        const centre = (n.y0 || 0) + ((n.y1 || 0) - (n.y0 || 0)) / 2;
        const placed = Math.max(centre, lastY + LABEL_MIN_GAP);
        labelOffsets.set(n.node_id, placed - (n.y0 || 0)); // store relative to node top
        lastY = placed;
      });
    });

    nodeRects
      .append('text')
      .attr('x', (d) => {
        const nodeWidth = (d.x1 || 0) - (d.x0 || 0);
        // Position labels outside the node
        if (d.layer <= 1) {
          return -6; // Left side for early layers
        }
        return nodeWidth + 6; // Right side for later layers
      })
      .attr('y', (d) =>
        labelOffsets.get(d.node_id) ?? ((d.y1 || 0) - (d.y0 || 0)) / 2
      )
      .attr('dy', '0.35em')
      .attr('text-anchor', (d) => (d.layer <= 1 ? 'end' : 'start'))
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .text((d) => {
        const label = d.label;
        // Truncate long labels
        return label.length > 20 ? label.substring(0, 18) + '...' : label;
      });

    // Add layer headers
    const layerPositions = new Map<number, number>();
    allRenderNodes.forEach((node) => {
      if (!layerPositions.has(node.layer)) {
        layerPositions.set(node.layer, node.x0 || 0);
      }
    });

    g.append('g')
      .attr('class', 'layer-headers')
      .selectAll('text')
      .data(Array.from(layerPositions.entries()).filter(([layer]) => LAYER_NAMES[layer]))
      .join('text')
      .attr('x', ([, x]) => x + 10)
      .attr('y', -15)
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#6b7280')
      .text(([layer]) => LAYER_NAMES[layer]);

  }, [nodes, links, dimensions, onNodeClick, onLinkClick]);

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-neutral-500">
        <p>No data to display. Generate data flow first.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className="overflow-visible"
      />

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className="absolute bg-white p-3 rounded-lg shadow-lg border border-neutral-200 z-50 pointer-events-none"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 10,
            transform: 'translate(-50%, -100%)',
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default D3SankeyDiagram;
